import { Flex, Pagination, Spin } from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Filter from 'omega-rtc/components/form/ucd/Filter';
import UCDCampaignTable from './UCDCampaignTable';
import NoContentPlaceHolder from 'omega-rtc/components/form/ucd/common/NoContentPlaceHolder';
import NoCampaignsPlaceholderIcon from 'img/ucd_no_campaigns_placeholder.svg';
import { searchUCDCampaigns } from 'omega-rtc/actions/ucd';
import { CAMPAIGN_DEFAULT_PAGE_SIZE, CAMPAIGN_QUERY_KEYS, FILTER_FIELDS } from 'omega-rtc/constants/ucd';
import {
  generateSearchCampaignRequestBody,
  getCountryOptions,
  getObjectValue,
  getPositiveNumber,
  updateSearchView,
} from 'omega-rtc/utils/ucd-utils';
import { hasPermission } from 'utils/user-utils';
import { UcdProjectResourceName, UcdUniversalScope } from 'omega-rtc/data/trident-campaigns';

interface FilterCondition {
  lhs: string;
  operator: string;
  rhs: any;
}

interface User {
  email: string;
  [key: string]: any;
}

interface SearchParams {
  [key: string]: any;
}

interface Props {
  router: any; // used to change path
  searchParams: SearchParams; // contains all the query parameters
  projectID: string; // populate this value if the search is constrained within one project only
  user: User; // the current user
}

const CampaignSearchView: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const campaigns = useSelector((state: any) => _.get(state, 'ucd.campaigns', []));
  const countries = useSelector((state: any) => state.countries.entities);
  const campaignSearchHits = useSelector((state: any) => state.ucd.campaignSearchHits);
  const searchCampaignsRequest = useSelector((state: any) => _.get(state.requests, 'searchUcdCampaignsRequest', {}));
  const { searchParams, router, projectID, user } = props;

  const onMoreFilterChange = (filters: FilterCondition[]) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.moreFilters, filters, router);

  const onCountryChange = (countries: string[]) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.country, countries, router);

  const onMyProjectChange = (value: boolean) => {
    // When turning on "My Campaigns", turn off "Pending Approval"
    const updatedParams = { ...searchParams };
    if (value) {
      updatedParams[CAMPAIGN_QUERY_KEYS.pendingApproval] = false;
    }
    updatedParams[CAMPAIGN_QUERY_KEYS.ownership] = value;
    router.push({
      pathname: router.location.pathname,
      query: updatedParams,
    });
  };

  const onPendingApprovalChange = (value: boolean) => {
    // When turning on "Pending Approval", turn off "My Campaigns"
    const updatedParams = { ...searchParams };
    if (value) {
      updatedParams[CAMPAIGN_QUERY_KEYS.ownership] = false;
    }
    updatedParams[CAMPAIGN_QUERY_KEYS.pendingApproval] = value;
    router.push({
      pathname: router.location.pathname,
      query: updatedParams,
    });
  };

  const onStatusChange = (status: string) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.status, status, router);

  const onVerticalsChange = (verticals: string[]) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.vertical, verticals, router);

  const onPageChange = (page: number) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.page, page, router);

  const onSearchTextChange = (searchText: string) => updateSearchView(searchParams, CAMPAIGN_QUERY_KEYS.search, searchText, router);

  useEffect(() => {
    dispatch(searchUCDCampaigns(
      generateSearchCampaignRequestBody(searchParams, user.email, projectID)
    ));
  }, [searchParams, user.email, projectID, dispatch]);

  const shouldFetchEntities = (currentProps: Props, nextProps: Props): boolean => {
    const allKeys = [..._.keys(currentProps.searchParams), ..._.keys(nextProps.searchParams)];
    const allCampaignKeys = _.filter(allKeys, k => _.startsWith(k, 'campaign_'));
    return _.some(
      _.uniq(allCampaignKeys),
      (key) => (currentProps.searchParams[key] !== nextProps.searchParams[key])
    );
  };

  useEffect(() => {
    if (shouldFetchEntities(props, props)) {
      dispatch(searchUCDCampaigns(
        generateSearchCampaignRequestBody(props.searchParams, props.user.email, props.projectID)
      ));
    }
  }, [props, dispatch]);

  const page = getPositiveNumber(_.get(searchParams, CAMPAIGN_QUERY_KEYS.page), 1);
  const pageSize = getPositiveNumber(_.get(searchParams, CAMPAIGN_QUERY_KEYS.pageSize), CAMPAIGN_DEFAULT_PAGE_SIZE);

  // Check if user has permission to approve campaigns
  // TODO: Use UcdCampaignResourceName instead of UcdProjectResourceName once the permission is updated in production
  // Reason: UcdCampaignResourceName-active is not a part of the permission list yet, so we are using UcdProjectResourceName-active instead
  const hasActivePermission = hasPermission(user.userPermissions, UcdProjectResourceName, UcdUniversalScope, 'active');

  const filterFields = [FILTER_FIELDS.myCampaign];
  if (hasActivePermission) {
    filterFields.push(FILTER_FIELDS.pendingApproval);
  }
  filterFields.push(FILTER_FIELDS.vertical, FILTER_FIELDS.status);
  if (_.isEmpty(projectID)) {
    filterFields.push(FILTER_FIELDS.country);
  }

  return (
    <React.Fragment>
      <Filter
        type="campaigns"
        router={router}
        countries={getCountryOptions(countries)}
        moreFiltersCallback={onMoreFilterChange}
        countryChangeCallback={onCountryChange}
        ownershipChangeCallback={onMyProjectChange}
        pendingApprovalChangeCallback={onPendingApprovalChange}
        statusChangeCallback={onStatusChange}
        verticalChangeCallback={onVerticalsChange}
        searchTextChangeCallback={onSearchTextChange}
        searchTextInitialValue={_.get(searchParams, CAMPAIGN_QUERY_KEYS.search)}
        countryInitialValue={getObjectValue(searchParams, CAMPAIGN_QUERY_KEYS.country)}
        ownershipInitialValue={_.get(searchParams, CAMPAIGN_QUERY_KEYS.ownership) === 'true'}
        pendingApprovalInitialValue={_.get(searchParams, CAMPAIGN_QUERY_KEYS.pendingApproval) === 'true'}
        statusInitialValue={_.get(searchParams, CAMPAIGN_QUERY_KEYS.status)}
        moreFiltersInitialValue={getObjectValue(searchParams, CAMPAIGN_QUERY_KEYS.moreFilters)}
        verticalsInitialValue={getObjectValue(searchParams, CAMPAIGN_QUERY_KEYS.vertical)}
        filterFields={filterFields}
      />
      <p style={{ height: '10px' }} />
      {
        searchCampaignsRequest.isPending ?
          <Flex justify="center" align="center" style={{ height: '50vh' }}>
            <Spin />
          </Flex>
          :
          <React.Fragment>
            {
              campaigns && campaigns.length > 0 ?
                <React.Fragment>
                  <Flex vertical className="campaign-listing-panel">
                    <UCDCampaignTable campaigns={campaigns} />
                    <Flex justify="flex-end" style={{ width: '100%' }}>
                      <Pagination
                        style={{
                          padding: 10,
                          float: 'right',
                        }}
                        size="small"
                        showSizeChanger={false}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                        current={page}
                        pageSize={pageSize}
                        total={campaignSearchHits}
                        onChange={onPageChange}
                      />

                    </Flex>
                  </Flex>
                </React.Fragment> : <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '105px' }}>
                  <NoContentPlaceHolder
                    icon={NoCampaignsPlaceholderIcon}
                    content={'No campaigns found'}
                    subContent={'Type in the description/code of existing campaigns or try modifying your search filters.'}
                  />
                </div>
            }
          </React.Fragment>
      }
    </React.Fragment>
  );
};

export default CampaignSearchView;

